import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';

/**
 * Running totals for a purchase or purchase-return, shown at the top of the
 * screen so the figures stay visible instead of sitting below a long item list.
 *
 * Used by the purchase and purchase-return forms and detail pages, which is why
 * it takes a plain `details` array rather than reading any one screen's state.
 *
 * `sticky` is opt-in because it only works inside an element that actually
 * scrolls. Layout.jsx's <main className="flex-1 overflow-auto"> sits in a
 * min-h-screen (not h-screen) wrapper, so it never constrains its own height —
 * the document scrolls instead, and `position: sticky` silently does nothing
 * app-wide. Pass sticky only when the bar sits inside a real scrollport.
 */
const TotalsBar = ({
  details,
  overallDiscount = 0,
  sticky = false,
  importResult,
  onDismissImportResult,
  onShowFailed,
}) => {
  const { formatPrice } = useCurrency();

  // One pass feeding every figure, so they cannot disagree with each other.
  const stats = useMemo(() => {
    const rows = details || [];
    let quantity = 0;
    let subtotal = 0;

    rows.forEach((detail) => {
      const q = parseFloat(detail?.Quantity) || 0;
      const price = parseFloat(detail?.Price) || 0;
      const lineDiscount = parseFloat(detail?.Discount) || 0;
      quantity += q;
      subtotal += (price - lineDiscount) * q;
    });

    const discount = parseFloat(overallDiscount) || 0;
    return { lineItems: rows.length, quantity, subtotal, discount, total: subtotal - discount };
  }, [details, overallDiscount]);

  return (
    <div
      className={`${sticky ? 'sticky top-0 z-30 ' : ''}bg-white rounded-xl shadow-sm border border-gray-200 p-3`}
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <div>
          <div className="text-xs text-gray-500">Line items</div>
          <div className="text-lg font-semibold text-gray-900">{stats.lineItems}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Total quantity</div>
          <div className="text-lg font-semibold text-gray-900">{stats.quantity}</div>
        </div>

        {/* Only worth the space when a discount actually applies. */}
        {stats.discount !== 0 && (
          <>
            <div>
              <div className="text-xs text-gray-500">Subtotal</div>
              <div className="text-lg font-semibold text-gray-900">
                {formatPrice(stats.subtotal)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Discount</div>
              <div className="text-lg font-semibold text-gray-900">
                {formatPrice(stats.discount)}
              </div>
            </div>
          </>
        )}

        <div>
          <div className="text-xs text-gray-500">Total amount</div>
          <div className="text-lg font-semibold text-gray-900">{formatPrice(stats.total)}</div>
        </div>

        {importResult && (
          <div className="ml-auto flex items-center gap-2 text-sm">
            <span className="px-2 py-1 rounded-full bg-green-50 text-green-700">
              ✅ {importResult.imported} imported
            </span>
            {importResult.failed > 0 && (
              <button
                type="button"
                onClick={onShowFailed}
                className="px-2 py-1 rounded-full bg-red-50 text-red-700 hover:bg-red-100 underline-offset-2 hover:underline"
              >
                ❌ {importResult.failed} failed
              </button>
            )}
            {importResult.skipped > 0 && (
              <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                {importResult.skipped} skipped
              </span>
            )}
            <button
              type="button"
              onClick={onDismissImportResult}
              className="p-1 text-gray-400 hover:text-gray-600"
              aria-label="Dismiss import result"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TotalsBar;
